<script setup>
import UnhideAllActionButton from "@/components/actions/partials/UnhideAllActionButton.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed} from "vue";
import ActionCategoryIcon from "@/components/actions/partials/ActionCategoryIcon.vue";
import {errorToast, successToast} from "@/composables/useToasts";

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const queryClient = useQueryClient();

const {mutate: unhideAction} = useMutation({
  mutationFn: templateId => axios.post(`/ajax/v2/actions/template/${templateId}/unhide`),
  onMutate: async (templateId) => {
    await queryClient.cancelQueries({queryKey: ['hidden-actions-by-template']});
    await queryClient.cancelQueries({queryKey: ['action-categories']});
    await queryClient.cancelQueries({queryKey: ['actions']});

    const category = props.template.category;

    const previousActions = queryClient.getQueryData(['hidden-actions-by-template', category]);

    queryClient.setQueryData(['hidden-actions-by-template', category], (old) => old ? old.filter(template => template.action_template_id !== templateId) : []);

    // remove category if the last item is removed
    const actionsInCategory = previousActions.filter(template => template.category === category);
    const isLastItemInCategory = actionsInCategory.length === 1 && actionsInCategory[0].action_template_id === templateId;

    if (isLastItemInCategory) {
      queryClient.setQueryData(['action-categories', true], (oldCategoriesData) => {
        if (!oldCategoriesData) {
          return [];
        }

        return oldCategoriesData.map(categoryParam => categoryParam.category === category
            ? { ...categoryParam, actions_count: 0 }
            : categoryParam
        );
      });
    }

    successToast('Actions unhidden by type');

    return {previousActions, isLastItemInCategory}
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;

    errorToast(message ?? 'Something went wrong unhiding actions for type');

    queryClient.setQueryData(['hidden-actions-by-template'], context.previousActions);
  },
  onSettled: (data, error, variables, context) => {
    queryClient.invalidateQueries({queryKey: ['actions']});
    queryClient.invalidateQueries({queryKey: ['hidden-actions-by-template']});
    if (context?.isLastItemInCategory) {
      queryClient.invalidateQueries({queryKey: ['action-categories']});
    }
  },
});

const actionText = computed(() => props.template.action_count === 1 ? '1 Action' : `${props.template.action_count} Actions`);
</script>

<template>
  <div
    class="tw-border tw-rounded-ld tw-border-grey-mid tw-rounded-lg tw-bg-white tw-w-full"
  >
    <div class="tw-flex tw-gap-6 tw-p-6 tw-items-center tw-justify-between">
      <div class="tw-flex tw-gap-6 tw-items-center">
        <ActionCategoryIcon :category="template.category" />

        {{ template.display_action_template_id }}
      </div>

      <div class="tw-flex tw-items-center tw-gap-6">
        <span class="tw-flex tw-items-center tw-gap-2 tw-text-grey">
          <svg-vue
            icon="icons/signal-bars"
            class="tw-w-5 tw-h-5 tw-flex-shrink-0"
          />
          <span class="tw-text-base">
            {{ actionText }}
          </span>
        </span>
        <UnhideAllActionButton @unhide="unhideAction(template.action_template_id)" />
      </div>
    </div>
  </div>
</template>
